import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { environment } from 'src/environments/environment';
import { SignalRConstants } from './signalr.constants';
import { BackendMessage } from './signalr.types';

@Injectable({
  providedIn: 'root',
})
export class SignalrService {

  private hubConnection!: HubConnection;

  /** Estado actual de la conexión */
  private connectionStateSubject = new BehaviorSubject<boolean>(false);
  connectionState$: Observable<boolean> = this.connectionStateSubject.asObservable();

  /** Único Subject: el backend envía un único evento "BackendMessage" con un campo type */
  private messageSubject = new Subject<BackendMessage>();
  onMessage$: Observable<BackendMessage> = this.messageSubject.asObservable();

  /** Grupos a los que el cliente está suscrito (para resuscribir al reconectar) */
  private subscribedJobs = new Set<string>();
  private subscribedEntities = new Set<string>();
  private subscribedUserId: number | null = null;

  // ─── Conexión ───────────────────────────────────────────────────────

  /** Inicia la conexión al Hub */
  async startConnection(): Promise<void> {
    if (this.hubConnection?.state === HubConnectionState.Connected) {
      return;
    }

    const token = localStorage.getItem(AuthenticationContants.keyTokenLocalStorage);

    this.hubConnection = new HubConnectionBuilder()
      .withUrl(`${environment.apiCervinoBackend.replace(/\/+$/, '')}${SignalRConstants.hubUrl}`, {
        accessTokenFactory: () => token ?? '',
      })
      .withAutomaticReconnect(SignalRConstants.reconnectDelays)
      .configureLogging(LogLevel.Warning)
      .build();

    this.registerHandlers();

    try {
      await this.hubConnection.start();
      this.connectionStateSubject.next(true);
      this.resubscribeAll();
    } catch (error) {
      console.error('Error al conectar SignalR:', error);
      this.connectionStateSubject.next(false);
    }
  }

  /** Detiene la conexión al Hub */
  async stopConnection(): Promise<void> {
    this.subscribedJobs.clear();
    this.subscribedEntities.clear();
    this.subscribedUserId = null;

    if (this.hubConnection?.state !== HubConnectionState.Disconnected) {
      await this.hubConnection.stop();
    }
    this.connectionStateSubject.next(false);
  }

  /** Indica si está conectado */
  get isConnected(): boolean {
    return this.hubConnection?.state === HubConnectionState.Connected;
  }

  // ─── Suscripción a grupos ────────────────────────────────────────────

  /** Suscribirse al progreso de un job */
  async subscribeToJob(jobId: string): Promise<void> {
    this.subscribedJobs.add(jobId);
    await this.invokeIfConnected('SubscribeToJob', jobId);
  }

  /** Desuscribirse del progreso de un job */
  async unsubscribeFromJob(jobId: string): Promise<void> {
    this.subscribedJobs.delete(jobId);
    await this.invokeIfConnected('UnsubscribeFromJob', jobId);
  }

  /** Suscribirse a notificaciones del usuario */
  async subscribeToNotifications(userId: number): Promise<void> {
    this.subscribedUserId = userId;
    await this.invokeIfConnected('SubscribeToNotifications', userId);
  }

  /** Suscribirse a cambios de una entidad */
  async subscribeToEntity(entity: string): Promise<void> {
    this.subscribedEntities.add(entity);
    await this.invokeIfConnected('SubscribeToEntity', entity);
  }

  /** Desuscribirse de cambios de una entidad */
  async unsubscribeFromEntity(entity: string): Promise<void> {
    this.subscribedEntities.delete(entity);
    await this.invokeIfConnected('UnsubscribeFromEntity', entity);
  }

  // ─── Privado ─────────────────────────────────────────────────────────

  /** Registra los handlers de eventos del servidor */
  private registerHandlers(): void {
    // Único evento del backend: BackendMessage con campo type
    this.hubConnection.on('BackendMessage', (message: BackendMessage) => {
      this.messageSubject.next(message);
    });

    // Reconexión automática: resuscribir grupos activos
    this.hubConnection.onreconnected(async () => {
      this.connectionStateSubject.next(true);
      await this.resubscribeAll();
    });

    this.hubConnection.onreconnecting(() => {
      this.connectionStateSubject.next(false);
    });

    this.hubConnection.onclose(() => {
      this.connectionStateSubject.next(false);
    });
  }

  /** Invoca un método en el Hub solo si está conectado */
  private async invokeIfConnected(method: string, ...args: any[]): Promise<void> {
    if (this.isConnected) {
      try {
        await this.hubConnection.invoke(method, ...args);
      } catch (error) {
        console.warn(`SignalR: error al invocar ${method}:`, error);
      }
    }
  }

  /** Resuscribe a todos los grupos activos tras reconexión */
  private async resubscribeAll(): Promise<void> {
    for (const jobId of this.subscribedJobs) {
      await this.invokeIfConnected('SubscribeToJob', jobId);
    }
    for (const entity of this.subscribedEntities) {
      await this.invokeIfConnected('SubscribeToEntity', entity);
    }
    if (this.subscribedUserId !== null) {
      await this.invokeIfConnected('SubscribeToNotifications', this.subscribedUserId);
    }
  }

}
